/**
 * Deciding which of the files an HR picked are résumés we will upload, and why the rest are not.
 *
 * The multi-select bulk import is won or lost here, the same way the zip import is won or lost in
 * zipEntries. Pulled out of the component so the rules -- accepted type, per-file size, and the
 * batch cap -- can be stated and tested without a browser or a file picker.
 *
 * A rejected file is never silently dropped: each carries the reason it was set aside, so the card
 * can tell the HR "over 5 MB" or "not a PDF or DOCX" rather than just quietly importing fewer than
 * they chose.
 */

export const RESUME_ALLOWED_EXT = ['.pdf', '.docx'] as const;

/** 5 MB per résumé. A PDF or DOCX CV is almost always well under 1 MB; this discards the scans and
 *  the mis-picked files rather than paying to extract them. */
export const RESUME_MAX_BYTES = 5 * 1024 * 1024;

/** At most fifty in one go. Beyond this the run is long enough that a second batch is the honest UI. */
export const RESUME_MAX_COUNT = 50;

export type RejectReason = 'type' | 'size' | 'over-limit' | 'empty';

export interface RejectedFile {
  name: string;
  reason: RejectReason;
}

export interface FileSelection {
  accepted: File[];
  rejected: RejectedFile[];
}

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

/**
 * Partition a picked set into what we will upload and what we will not.
 *
 * Order matters and is deliberate: a file is judged on type, then size, then the batch cap, so the
 * reason shown is the first thing actually wrong with it. The cap is applied last and only to files
 * that would otherwise be accepted, so ten oversize files do not use up the fifty slots.
 */
export function partitionResumeFiles(
  files: File[],
  opts: { maxBytes?: number; maxCount?: number; allowedExt?: readonly string[] } = {},
): FileSelection {
  const maxBytes = opts.maxBytes ?? RESUME_MAX_BYTES;
  const maxCount = opts.maxCount ?? RESUME_MAX_COUNT;
  const allowedExt = opts.allowedExt ?? RESUME_ALLOWED_EXT;

  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];

  for (const file of files) {
    if (!allowedExt.includes(extensionOf(file.name))) {
      rejected.push({ name: file.name, reason: 'type' });
      continue;
    }
    if (file.size === 0) {
      rejected.push({ name: file.name, reason: 'empty' });
      continue;
    }
    if (file.size > maxBytes) {
      rejected.push({ name: file.name, reason: 'size' });
      continue;
    }
    if (accepted.length >= maxCount) {
      rejected.push({ name: file.name, reason: 'over-limit' });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}

/** Human copy for a rejection, for the discarded-files note. */
export function rejectReasonLabel(reason: RejectReason, maxBytes = RESUME_MAX_BYTES): string {
  switch (reason) {
    case 'type':
      return 'not a PDF or DOCX';
    case 'size':
      return `over ${Math.round(maxBytes / 1024 / 1024)} MB`;
    case 'over-limit':
      return `over the ${RESUME_MAX_COUNT}-file limit`;
    case 'empty':
      return 'empty file';
  }
}
